/**
 * DRE Audit history — list + detail hooks for the gerencial audit panel.
 *
 * Backend: see `monumenta-trpc/handlers/fluxo-gerencial/router.ts`
 *   - dreAuditList    (paginated, ordered by createdAt desc)
 *   - dreAuditGetById (snapshot — KPI aggregates + per-period changes only)
 *
 * Snapshot stores ONLY what changed between the pre-mutation and post-refresh
 * captures: per-period lineItem/clientRevenue/categoria/matrix changes, plus
 * a top-level KPI aggregate. The unchanged baseline is dropped to keep the
 * DDB payload small and to match the "what mudou nesse import?" UX intent.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { get } from 'aws-amplify/api'

export interface CachePeriod {
  year: number
  month: number
}

export type AuditSource = 'processImport' | 'updateMany'
export type AuditStatus = 'pending' | 'complete' | 'failed'

export interface AuditTrigger {
  requestId: string
  importacaoId?: number
  fileName?: string
  rowCount?: number
  updatedCount?: number
  deletedCount?: number
}

export interface DreAuditListItem {
  id: string
  createdAt: string
  source: AuditSource
  trigger: AuditTrigger
  affectedPeriods: CachePeriod[]
  status: AuditStatus
}

export interface DreAuditListResponse {
  items: DreAuditListItem[]
  nextCursor?: string
}

// ---------------------------------------------------------------------------
// "Changes-only" shapes — match the backend Zod schemas in fluxo-gerencial.ts.
// ---------------------------------------------------------------------------

export interface DreAuditLineItemChange {
  code: string
  label: string
  type: string
  level: number
  parentCode: string | null
  before: number
  after: number
}

export interface DreAuditClientRevenueChange {
  codigoCliente: string | null
  nomeCliente: string
  before: number
  after: number
}

export interface DreAuditCategoriaChange {
  codigoGerencial: string | null
  relacaoGerencial: string
  before: number
  after: number
}

export interface DreAuditMatrixProjectChange {
  job: string
  receitaBefore: number
  receitaAfter: number
  custoBefore: number
  custoAfter: number
}

export interface DreAuditMatrixClientChange {
  codigoCliente: string | null
  nomeCliente: string
  receitaBefore: number
  receitaAfter: number
  custoBefore: number
  custoAfter: number
  projetos: DreAuditMatrixProjectChange[]
}

export interface DreAuditPeriodChanges {
  year: number
  month: number
  lineItemChanges: DreAuditLineItemChange[]
  clientRevenueChanges: DreAuditClientRevenueChange[]
  categoriaChanges: DreAuditCategoriaChange[]
  matrixChanges: DreAuditMatrixClientChange[]
}

export interface DreAuditKpiAggregate {
  code: string
  label: string
  before: number
  after: number
}

export interface DreAuditSnapshot {
  id: string
  createdAt: string
  source: AuditSource
  trigger: AuditTrigger
  affectedPeriods: CachePeriod[]
  capturedRange: { startYear: number; endYear: number }
  status: AuditStatus
  /** Present only when status === 'complete'. */
  kpis?: DreAuditKpiAggregate[]
  /** Present only when status === 'complete'. */
  periodChanges?: DreAuditPeriodChanges[]
  finalizedAt?: string
  errorMessage?: string
}

async function fluxoGet<T>(path: string, params?: Record<string, string>): Promise<T> {
  const queryString = params ? '?' + new URLSearchParams(params).toString() : ''
  const fullPath = `/fluxoGerencial${path}${queryString}`
  const restOperation = get({ apiName: 'monumenta-trpc', path: fullPath })
  const response = await restOperation.response
  const body = await response.body.text()
  const parsed = JSON.parse(body)
  if (parsed.result?.data) return parsed.result.data
  if (parsed.error) throw new Error(parsed.error.message || 'Unknown error')
  return parsed
}

export function useDreAuditList(params: { limit?: number; cursor?: string; enabled?: boolean }) {
  const limit = params.limit ?? 20
  const input: Record<string, unknown> = { limit }
  if (params.cursor) input.cursor = params.cursor

  return useQuery<DreAuditListResponse>({
    queryKey: ['dreAudit', 'list', input],
    queryFn: () => fluxoGet('/dreAuditList', { input: JSON.stringify(input) }),
    enabled: params.enabled ?? true,
  })
}

export function useDreAuditGetById(id: string | null): UseQueryResult<DreAuditSnapshot> {
  return useQuery<DreAuditSnapshot>({
    queryKey: ['dreAudit', 'getById', id],
    queryFn: () => fluxoGet('/dreAuditGetById', { input: JSON.stringify({ id }) }),
    enabled: !!id,
  })
}
